import React from "react";
import "./JobsView.scss";
import { notification, Tabs} from "antd";
import {TabPosition} from "rc-tabs/lib/interface";
import {TabsType} from "antd/lib/tabs";
import {getTranslate, Translate, TranslateFunction} from "react-localize-redux";
import {connect} from "react-redux";
import {Nullable} from "@data-force/app";
import {ArgsProps} from "antd/lib/notification";
import StoreState from "../../interfaces/store/storeState";
import {JobType, JobFilterType, JobNotification} from "../../enums/enums";
import JobList from "../../components/JobList/JobList";
import {
    getPhaseConfigList,
    resetJobNotification,
    setActiveJobType,
    setJobFilterType,
} from "../../store/actions/job";
import {
    GetPhaseConfigListAction,
    ResetJobNotificationAction,
    SetActiveJobTypeAction,
    SetJobFilterTypeAction,
} from "../../interfaces/store/actions/job";
import JobDrawer from "../../components/JobDrawer/JobDrawer";
import {JobNotificationData} from "../../interfaces/store/actions/payload/job";
import {ErrorIcon, SuccessIcon} from "../../assets/Icons";

const {TabPane} = Tabs;

interface StateProps {
    translate: TranslateFunction
    activeJobType: string
    filter: string
    notificationData: Nullable<JobNotificationData>
    isMobile: boolean
}

interface DispatchProps {
    getPhaseConfigList(): GetPhaseConfigListAction

    setActiveJobType(payload: string): SetActiveJobTypeAction

    setJobFilterType(payload: string): SetJobFilterTypeAction

    resetJobNotification(): ResetJobNotificationAction
}

type Props = StateProps & DispatchProps;

class JobsView extends React.Component<Props> {
    private static readonly translationPath: string = "pages.JobsView.JobsView";

    constructor(props: Props) {
        super(props);

        this.onChangeHandler = this.onChangeHandler.bind(this);

        this.getTabPanes = this.getTabPanes.bind(this);
    }

    componentDidMount(): void {
        this.props.getPhaseConfigList();
    }

    componentWillUnmount(): void {
        this.props.setActiveJobType(JobType.FEATURED);
        this.props.setJobFilterType(JobFilterType.CLAIMABLE);
    }

    /**
     * On tab change event, set store's current active job type
     * this allows us to keep track of type of loaded job list
     * @param {string} currentTabType
     */
    onChangeHandler(currentTabType: string): void {
        switch (currentTabType) {
            case JobType.FEATURED:
            case JobType.AVAILABLE:
                this.props.setJobFilterType(JobFilterType.CLAIMABLE);
                break;
            case JobType.IN_PROGRESS:
                this.props.setJobFilterType(JobFilterType.ONGOING);
                break;
            case JobType.COMPLETED:
                this.props.setJobFilterType(JobFilterType.DONE);
                break;
            default:
                break;
        }
        this.props.setActiveJobType(currentTabType);
    }

    /**
     * Generate tab panes for each of jobType enum members
     * @return {React.ReactNode}
     */
    getTabPanes(): React.ReactNode {
        const {translate, activeJobType, isMobile}: Props = this.props;
        const jobTypes: string[] = Object.keys(JobType);
        return jobTypes.map((jobType: string) => (
            <TabPane
                tab={translate(`${JobsView.translationPath}.${jobType}_TAB.text`)}
                key={jobType}
            >
                {activeJobType === jobType && (
                    <JobList isMobile={isMobile} key={activeJobType}/>
                )}
            </TabPane>
        ));
    }

    /**
     * Show notification with name and event type data, after notification is shown, we reset notification data.
     * @param {string} phaseName
     * @param {string} type
     */
    showNotification({phaseName, type}: JobNotificationData): void {
        const {translate}: Props = this.props;
        const msgPrefix: string = translate(`${JobsView.translationPath}.notificationPrefix.text`) as string;
        const msgSuffix: string = translate(`${JobsView.translationPath}.notificationSuffix.${type}.text`) as string;

        let args: ArgsProps = {
            className: "df-job-success-notification",
            message: (
                <span>
                    {msgPrefix}
                    <strong>{phaseName}</strong>
                    {msgSuffix}
                </span>
            ),
            icon: <SuccessIcon/>,
        };

        if (type === JobNotification.ERROR) {
            args = {
                className: "df-job-error-notification",
                message: translate(`${JobsView.translationPath}.notificationError.text`),
                icon: <ErrorIcon/>,
            };
        }

        notification.open({
            ...args,
            duration: 5,
        });
        this.props.resetJobNotification();
    }

    render(): React.ReactNode {
        const {
            activeJobType, notificationData, isMobile, translate,
        }: Props = this.props;
        const tabPosition: TabPosition = isMobile ? "top" : "left";
        const tabType: TabsType = isMobile ? "line" : "card";
        return (
            <div className="df-jobs-container">
                {notificationData && this.showNotification(notificationData)}
                <Tabs
                    tabPosition={tabPosition}
                    type={tabType}
                    onChange={this.onChangeHandler}
                    activeKey={activeJobType}
                    moreIcon={(
                        <span aria-label={translate(`${JobsView.translationPath}.moreTabs.text`) as string}>
                            <Translate id={`${JobsView.translationPath}.moreTabsContent.text`}/>
                        </span>
                    )}
                >
                    {this.getTabPanes()}
                </Tabs>
                <JobDrawer/>
            </div>
        );
    }
}

function mapStateToProps(state: StoreState): StateProps {
    return {
        translate: getTranslate(state.localize),
        activeJobType: state.job.activeJobType,
        filter: state.job.filter,
        notificationData: state.job.notificationData,
        isMobile: state.mobile.isMobile,
    };
}

const mapDispatchToProps: DispatchProps = {
    getPhaseConfigList,
    setActiveJobType,
    setJobFilterType,
    resetJobNotification,
};

export default connect(mapStateToProps, mapDispatchToProps)(JobsView);
